import { v4 } from "uuid";

import type { CuratedPlaceOfSafety } from "@thalamos/common";
import { IncidentWorkItemStatusSchema } from "@thalamos/common";
import {
  FormSectionSchema,
  IncidentFormIdSchema,
  IncidentFormTypeSchema,
  IncidentWorkItemIdSchema,
  type CreateIncidentMinimumFields,
  type ExtendedVisionUser,
  type IncidentWorkItemId,
} from "@thalamos/vision-common";

import api from "../api/index.js";
import { config } from "../config/index.js";
import {
  generateDemoDispatchNumber,
  getQuickCreateSampleData,
  type PersonName,
} from "./quickCreateSampleData.js";

/**
 * Quick create fabricates statutory records that no officer signed off, so it
 * must never be reachable in production.
 */
export const isQuickCreateEnabled = [
  "local",
  "dev",
  "docker",
  "staging",
].includes(config.environment);

/**
 * Whether the element takes text entry, so keystrokes there are input rather
 * than a shortcut.
 */
export function isTypingInto(element: Element | null): boolean {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  return (
    element.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(element.tagName)
  );
}

async function pickRandomPlaceOfSafety(): Promise<
  CuratedPlaceOfSafety | undefined
> {
  try {
    const response = await api.placeOfSafety.getAll();
    const placesOfSafety = response.data?.placesOfSafety ?? [];
    return placesOfSafety.length > 0
      ? placesOfSafety[Math.floor(Math.random() * placesOfSafety.length)]
      : undefined;
  } catch {
    console.warn(
      "Quick create 136: failed to fetch places of safety, using fallback",
    );
    return undefined;
  }
}

/**
 * Creates a 136 incident populated up to and including Place of Safety arrival,
 * but stopping before sharing with NHS, so testing the later steps does not
 * require completing a dozen form pages by hand.
 *
 * Anything the caller does not supply is fabricated: omit `name` or
 * `curatedPlaceOfSafety` and a random one is used.
 *
 * A testing aid: callers must gate on `isQuickCreateEnabled`. Throws on the
 * first failed step, leaving a part-built incident behind, so the caller can
 * report what went wrong.
 */
export async function runQuickCreate136({
  user,
  name,
  curatedPlaceOfSafety,
}: {
  user: ExtendedVisionUser;
  name?: PersonName;
  curatedPlaceOfSafety?: CuratedPlaceOfSafety;
}): Promise<IncidentWorkItemId> {
  const sessionContext = user.sessionContext;
  if (!sessionContext) {
    throw new Error("No user session context available");
  }

  const incidentWorkItemId = IncidentWorkItemIdSchema.parse(v4());
  const workflowVersion =
    user.sessionOrganisationConfiguration?.workflowVersion;

  if (workflowVersion === undefined) {
    throw new Error("Current organisation has no workflow version configured");
  }

  const sampleData = getQuickCreateSampleData(workflowVersion, {
    name,
    curatedPlaceOfSafety:
      curatedPlaceOfSafety ?? (await pickRandomPlaceOfSafety()),
  });

  // Step 1: Create incident work item
  // Sample data is validated against workflow schemas by tests in vision-common.
  // Its commandUnit is a placeholder uuid, so the session organisation replaces it.
  const createIncidentFormSection = {
    ...sampleData.createIncidentMain,
    dispatchNumber: { value: generateDemoDispatchNumber() },
    commandUnit: {
      value: sessionContext.organisationId,
      valueLabel: sessionContext.organisationName,
    },
  };
  const createResponse = await api.incidentWorkItem.create(incidentWorkItemId, {
    workflowVersion,
    // Sample sections are loosely typed; the server validates the section
    // against the workflow's own schema.
    createIncidentFormSection:
      createIncidentFormSection as unknown as CreateIncidentMinimumFields,
  });
  if (createResponse.status !== 204) {
    throw new Error(
      `Failed to create incident: ${String(createResponse.status)}`,
    );
  }

  // Step 2: Get incident to find triage form ID
  const getResponse1 = await api.incidentWorkItem.get(incidentWorkItemId);
  const triageForm = getResponse1.data?.forms.find(
    (f) => f.formType === IncidentFormTypeSchema.enum.triage,
  );
  if (!triageForm) {
    throw new Error("Triage form not found");
  }

  // Step 3: Submit triage (auto-creates the 136 form)
  const triageResponse = await api.incidentWorkItem.submitTriage(
    incidentWorkItemId,
    triageForm.id,
    FormSectionSchema.enum.triageMain,
    sampleData.triageMain,
  );
  if (triageResponse.status !== 204) {
    throw new Error(
      `Failed to submit triage: ${String(triageResponse.status)}`,
    );
  }

  // Step 4: Get incident again to find 136 form ID
  const getResponse2 = await api.incidentWorkItem.get(incidentWorkItemId);
  const section136Form = getResponse2.data?.forms.find(
    (f) => f.formType === IncidentFormTypeSchema.enum.section136,
  );
  if (!section136Form) {
    throw new Error("Section 136 form not found after triage submission");
  }

  // Step 5: Fill person details
  await api.incidentWorkItem.updateData(
    incidentWorkItemId,
    section136Form.id,
    FormSectionSchema.enum.personDetails,
    sampleData.personDetails,
    true,
  );

  // Step 6: Fill detention details
  // In BTP (and other workflows), orgResponsibleForDetention is pre-populated
  // from the create-incident commandUnit and can't be deselected. Mirror that
  // by overriding it to match the session organisation.
  await api.incidentWorkItem.updateData(
    incidentWorkItemId,
    section136Form.id,
    FormSectionSchema.enum.detentionDetails,
    {
      ...sampleData.detentionDetails,
      orgResponsibleForDetention: {
        value: sessionContext.organisationId,
        valueLabel: sessionContext.organisationName,
      },
    },
    true,
  );

  // Step 7: Fill information for healthcare
  await api.incidentWorkItem.updateData(
    incidentWorkItemId,
    section136Form.id,
    FormSectionSchema.enum.informationForHealthcare,
    sampleData.informationForHealthcare,
    true,
  );

  // Step 8: Create place of safety form
  const placeOfSafetyFormId = IncidentFormIdSchema.parse(v4());
  await api.incidentWorkItem.createPlaceOfSafetyForm(
    incidentWorkItemId,
    placeOfSafetyFormId,
  );

  // Step 9: Fill place of safety main section
  await api.incidentWorkItem.updateData(
    incidentWorkItemId,
    placeOfSafetyFormId,
    FormSectionSchema.enum.recordPlaceOfSafetyMain,
    sampleData.recordPlaceOfSafetyMain,
    true,
  );

  // Step 10: Fill place of safety arrival (marks arrival status)
  await api.incidentWorkItem.updateData(
    incidentWorkItemId,
    placeOfSafetyFormId,
    FormSectionSchema.enum.recordPlaceOfSafetyArrival,
    sampleData.recordPlaceOfSafetyArrival,
    true,
    IncidentWorkItemStatusSchema.enum["arrived-at-place-of-safety"],
  );

  return incidentWorkItemId;
}
